import React, { useState } from 'react';
import { User, Account } from '../../api/structs/user';

interface UserMenuProps {
  users: User[];
  activeUser: User | null;
  activeAccount: Account | null;
  onAddUser: () => void;
  onSelectUser: (user: number) => void;
  onRemoveUser: (userID: number) => void;
  onSelectAccount: (accountID: string) => void;
}

const UserMenu: React.FC<UserMenuProps> = ({ 
  users, 
  activeUser, 
  activeAccount,
  onAddUser, 
  onSelectUser,
  onRemoveUser,
  onSelectAccount
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  return (
    <div className="relative z-[150]">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center gap-2 px-3 py-1.5 border border-[var(--border-subtle)] bg-[var(--surface-1)] hover:bg-[var(--surface-2)] shadow-[2px_2px_0px_0px_var(--border-subtle)] text-[var(--app-text)] text-xs uppercase tracking-widest transition-colors h-full"
      >
        <div className="flex flex-col items-start leading-none">
          <span className="font-bold">{activeUser ? `@${activeUser.username}` : 'No User'}</span>
          {activeAccount && (
            <span className="text-[10px] text-[var(--app-text-muted)] mt-1">
              {activeAccount.is_ai && 'AI '}{activeAccount.username}
            </span>
          )}
        </div>
        <span className="text-[10px] text-[var(--app-text-muted)] font-bold">{showDropdown ? 'CLOSE' : 'OPEN'}</span>
      </button>

      {showDropdown && (
        <>
          <div className="fixed inset-0 z-[140]" onClick={() => setShowDropdown(false)} />
          <div className="absolute right-0 mt-2 w-80 bg-[var(--surface-2)] border border-[var(--border-strong)] shadow-[4px_4px_0_0_var(--border-strong)] z-[150] py-2">
            {/* Users */}
            {users.length > 0 && (
              <div className="max-h-96 overflow-y-auto">
                {users.map((user) => (
                  <div key={user.id} className="border-b border-[var(--border-subtle)] last:border-b-0">
                    {/* User Header */}
                    <div
                      className={`px-4 py-2 hover:bg-[var(--surface-3)] cursor-pointer flex items-center justify-between ${
                        activeUser?.id === user.id ? 'bg-[var(--surface-3)]' : ''
                      }`}
                      onClick={() => {
                        onSelectUser(user.id);
                      }}
                    >
                      <div>
                        <p className="text-[var(--app-text)] text-sm font-bold uppercase tracking-widest">
                          @{user.username}
                          {activeUser?.id === user.id && (
                            <span className="text-[var(--app-text-muted)] ml-2 text-[10px]">ACTIVE</span>
                          )}
                        </p>
                        <p className="text-[var(--app-text-muted)] text-[10px] uppercase tracking-widest mt-0.5">ID: {user.id}</p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveUser(user.id);
                        }}
                        className="text-[var(--app-text-muted)] hover:text-[#ef4444] text-[10px] font-bold uppercase tracking-widest px-2 py-1 border border-[var(--border-subtle)] bg-[var(--surface-1)] hover:border-[#ef4444]/50 transition-colors"
                      >
                        Remove
                      </button>
                    </div>

                    {/* Accounts (only show for active user) */}
                    {activeUser?.id === user.id && user.accounts && user.accounts.length > 0 && (
                      <div className="bg-[var(--surface-1)] px-2 py-2">
                        <p className="text-[var(--app-text-muted)] text-[10px] font-bold uppercase tracking-widest px-2 mb-2">Social Accounts:</p>
                        {user.accounts.map((account) => (
                          <div
                            key={account._id}
                            className={`flex items-center justify-between px-2 py-1.5 cursor-pointer border border-transparent ${
                              activeAccount?._id === account._id 
                                ? 'bg-[var(--surface-3)] border-[var(--border-subtle)]'
                                : 'hover:bg-[var(--surface-2)]'
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectAccount(account._id);
                            }}
                          >
                            <div className="flex items-center gap-2">
                              {account.is_ai && <span className="text-[10px] font-bold text-[var(--app-text-muted)]">AI</span>}
                              <span className="text-[var(--app-text)] text-xs font-bold uppercase tracking-widest">{account.username}</span>
                              <span className="text-[var(--app-text-muted)] text-[10px] uppercase tracking-widest">
                                {account.platforms.join(', ')}
                              </span>
                            </div>
                            {activeAccount?._id === account._id && (
                              <span className="text-[var(--app-text)] text-[10px] font-bold tracking-widest uppercase">Active</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {users.length > 0 && <div className="border-t border-[var(--border-subtle)] my-1" />}

            <button
              onClick={() => {
                onAddUser();
                setShowDropdown(false);
              }}
              className="w-full text-left px-4 py-2 text-[var(--app-text)] hover:bg-[var(--surface-3)] text-xs font-bold uppercase tracking-widest transition-colors"
            >
              Add User
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default UserMenu;
